import { put, select, race, take, takeLatest } from "redux-saga/effects";
import segment from "../../utils/segment";

export const actions = {
  ENABLE_FORM: "bonfire/reportGenerator/ENABLE_FORM",
  UPDATE_SELECTED_PROJECT: "bonfire/reportGenerator/UPDATE_SELECTED_PROJECT",
  GENERATE_REPORT: "bonfire/reportGenerator/GENERATE_REPORT",
  GENERATE_REPORT_REQUEST: "bonfire/reportGenerator/GENERATE_REPORT_REQUEST",
  GENERATE_REPORT_SUCCESS: "bonfire/reportGenerator/GENERATE_REPORT_SUCCESS",
  GENERATE_REPORT_FAILURE: "bonfire/reportGenerator/GENERATE_REPORT_FAILURE",
  GET_REPORT_STATUS_REQUEST:
    "bonfire/reportGenerator/GET_REPORT_STATUS_REQUEST",
  GET_REPORT_STATUS_SUCCESS:
    "bonfire/reportGenerator/GET_REPORT_STATUS_SUCCESS",
  GET_REPORT_STATUS_FAILURE:
    "bonfire/reportGenerator/GET_REPORT_STATUS_FAILURE",
};

export const jobStates = {
  RUNNING: "running",
  DONE_OK: "doneOk",
  DONE_ERROR: "doneErr",
  CANCELLING: "cancelling",
  DONE_CANCEL: "doneCancel",
};

const initialState = {
  isFormDisabled: false,
  selectedProject: null,
  showJobStatusPanel: false,
  currentJobStatus: {},
};

export function reducer(state = initialState, action) {
  switch (action.type) {
    case actions.ENABLE_FORM:
      return {
        ...state,
        isFormDisabled: false,
      };
    case actions.UPDATE_SELECTED_PROJECT:
      return {
        ...state,
        selectedProject: action.payload.project,
      };
    case actions.GENERATE_REPORT:
      return {
        ...state,
        isFormDisabled: true,
        showJobStatusPanel: true,
        currentJobStatus: { state: jobStates.RUNNING },
      };
    case actions.GENERATE_REPORT_SUCCESS:
      return {
        ...state,
        currentJobStatus: {
          ...state.currentJobStatus,
          id: action.result.jobId,
        },
      };
    case actions.GENERATE_REPORT_FAILURE:
      return {
        ...state,
        isFormDisabled: false,
        currentJobStatus: {
          state: jobStates.DONE_ERROR,
          msg: action.error.message || "An unknown error occurred",
        },
      };
    case actions.GET_REPORT_STATUS_SUCCESS:
      return {
        ...state,
        currentJobStatus: {
          ...state.currentJobStatus,
          start: action.result.start,
          state: action.result.state,
          msg: action.result.progressMsg,
          progress: action.result.progress,
        },
      };
    case actions.GET_REPORT_STATUS_FAILURE:
      return {
        ...state,
        isFormDisabled: false,
        currentJobStatus: {
          state: jobStates.DONE_ERROR,
          msg: action.error.message || "An unknown error occurred",
        },
      };
    default:
      return state;
  }
}

export function enableForm() {
  return {
    type: actions.ENABLE_FORM,
    payload: {},
  };
}

export function updateSelectedProject(project) {
  return {
    type: actions.UPDATE_SELECTED_PROJECT,
    payload: { project },
  };
}

export function generateReport(reportId, reportOptions) {
  return {
    type: actions.GENERATE_REPORT,
    payload: { reportId, reportOptions },
  };
}

export function getAuthUser(state) {
  return state.auth.user;
}

export function* pollForReportStatus(jobId, reportOptions) {
  const user = yield select(getAuthUser);
  const headers = { Authorization: `Bearer ${user.token}` };

  const requestAction = {
    types: [
      actions.GET_REPORT_STATUS_REQUEST,
      actions.GET_REPORT_STATUS_SUCCESS,
      actions.GET_REPORT_STATUS_FAILURE,
    ],
    promise: (client) => client.get(`/report/job/${jobId}`, { headers }),
  };
  yield put(requestAction);

  const { success } = yield race({
    success: take(actions.GET_REPORT_STATUS_SUCCESS),
    failure: take(actions.GET_REPORT_STATUS_FAILURE),
  });

  if (success) {
    switch (success.result.state) {
      case jobStates.RUNNING:
      case jobStates.CANCELLING:
        yield new Promise((resolve) => {
          setTimeout(() => resolve(), 500);
        });
        yield pollForReportStatus(jobId, reportOptions);
        break;
      case jobStates.DONE_OK:
        segment.track("Report generated", { ...reportOptions });
        yield put(enableForm());
        break;
      case jobStates.DONE_CANCEL:
        segment.track("Report cancelled", { ...reportOptions });
        yield put(enableForm());
        break;
      case jobStates.DONE_ERROR:
        segment.track("Report errored", { ...reportOptions });
        yield put(enableForm());
        break;
      default:
        break;
    }
  }
}

export function* sagaGenerateReport(action) {
  const { reportId, reportOptions } = action.payload;
  const user = yield select(getAuthUser);

  const headers = { Authorization: `Bearer ${user.token}` };
  const requestAction = {
    types: [
      actions.GENERATE_REPORT_REQUEST,
      actions.GENERATE_REPORT_SUCCESS,
      actions.GENERATE_REPORT_FAILURE,
    ],
    promise: (client) =>
      client.post(`/report/${reportId}`, { data: reportOptions, headers }),
  };
  yield put(requestAction);

  const { success } = yield race({
    success: take(actions.GENERATE_REPORT_SUCCESS),
    failure: take(actions.GENERATE_REPORT_FAILURE),
  });

  if (success) yield pollForReportStatus(success.result.jobId, reportOptions);
}

export function* watchGenerateReport() {
  yield takeLatest(actions.GENERATE_REPORT, sagaGenerateReport);
}

export default reducer;
